import React, { useState } from "react";
import * as Mui from "@mui/material";
import flags from "react-phone-number-input/flags";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import {
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import { RequiredMark } from "components/DesignSystem/SettingsPageShell";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { designColors } from "services/constants/designTokens";
import {
  CONTACT_ROLES,
  DIAL_CODES,
  contactToDraft,
  emptyContact,
  validateContact,
} from "./contactRules";

const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: designColors.tealDark,
  },
};

function DialCode({ entry }) {
  const Flag = flags[entry?.iso];
  return (
    <Mui.Stack direction="row" spacing={1} alignItems="center">
      <Mui.Box
        component="span"
        aria-hidden="true"
        sx={{ display: "inline-flex", width: 22, "& svg": { width: "100%" } }}
      >
        {Flag && <Flag title={entry.country} />}
      </Mui.Box>
      <span>{entry?.code}</span>
    </Mui.Stack>
  );
}

// Mounted for each Add/Edit action, so cancelled drafts never leak into a new form.
export default function ContactFormPanel({
  contact,
  contacts,
  onClose,
  onSave,
  saving = false,
  error,
  expanded,
  onToggleExpand,
}) {
  const [draft, setDraft] = useState(() =>
    contact ? contactToDraft(contact) : emptyContact(),
  );
  const [errors, setErrors] = useState({});
  const dialCodes = [...DIAL_CODES];
  [draft.phoneCode, draft.whatsappCode].forEach((code) => {
    if (!dialCodes.some((entry) => entry.code === code))
      dialCodes.push({ code, country: code });
  });
  const update = (key, value) => {
    setDraft((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({
      ...previous,
      [key === "phoneCode"
        ? "phone"
        : key === "whatsappCode"
          ? "whatsapp"
          : key]: "",
    }));
  };
  const save = (event) => {
    event.preventDefault();
    if (saving) return;
    const nextErrors = validateContact(draft, contacts);
    setErrors(nextErrors);
    if (!Object.keys(nextErrors).length) onSave(draft);
  };
  const field = (key, label, placeholder, type = "text", required = true) => (
    <Mui.Box>
      <GlobalFieldLabel>
        {label}
        {required && <RequiredMark />}
      </GlobalFieldLabel>
      <Mui.TextField
        fullWidth
        size="small"
        disabled={saving}
        type={type}
        value={draft[key]}
        placeholder={placeholder}
        onChange={(event) => update(key, event.target.value)}
        error={Boolean(errors[key])}
        helperText={errors[key]}
        inputProps={{ "aria-label": label, "aria-required": required }}
        sx={fieldSx}
      />
    </Mui.Box>
  );
  return (
    <GlobalSidePanel
      open
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title={contact ? "Edit Cauver Contact" : "Add Cauver Contact"}
      subtitle="This contact will be visible to customer organisations."
      breadcrumb={["System Configuration", "Cauver Contact"]}
      bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
      footer={
        <PanelActionFooter
          onCancel={onClose}
          primaryLabel={
            saving ? "Saving..." : contact ? "Update Contact" : "Save Contact"
          }
          primaryProps={{
            type: "submit",
            form: "cauver-contact-form",
            disabled: saving,
          }}
        />
      }
    >
      <Mui.Stack
        component="form"
        id="cauver-contact-form"
        noValidate
        onSubmit={save}
        spacing={2.5}
      >
        {error && <Mui.Alert severity="error">{error}</Mui.Alert>}
        {field("name", "Name", "Enter full name")}
        {field(
          "designation",
          "Designation",
          "Enter designation",
          "text",
          false,
        )}
        <Mui.Box>
          <GlobalFieldLabel>
            Role
            <RequiredMark />
          </GlobalFieldLabel>
          <Mui.TextField
            select
            fullWidth
            size="small"
            disabled={saving}
            value={draft.role}
            onChange={(event) => update("role", event.target.value)}
            error={Boolean(errors.role)}
            helperText={errors.role}
            inputProps={{ "aria-label": "Role", "aria-required": true }}
            SelectProps={{ displayEmpty: true }}
            sx={fieldSx}
          >
            <Mui.MenuItem value="" disabled>
              Select role
            </Mui.MenuItem>
            {[...new Set([...CONTACT_ROLES, draft.role].filter(Boolean))].map(
              (role) => (
                <Mui.MenuItem key={role} value={role}>
                  {role}
                </Mui.MenuItem>
              ),
            )}
          </Mui.TextField>
        </Mui.Box>
        {field("email", "Email", "Enter email address", "email")}
        <Mui.Box>
          <GlobalFieldLabel>
            Phone
            <RequiredMark />
          </GlobalFieldLabel>
          <Mui.Stack direction="row" spacing={1}>
            <Mui.TextField
              select
              size="small"
              disabled={saving}
              value={draft.phoneCode}
              onChange={(event) => update("phoneCode", event.target.value)}
              inputProps={{ "aria-label": "Phone country code" }}
              SelectProps={{
                renderValue: (code) => (
                  <DialCode
                    entry={dialCodes.find((entry) => entry.code === code)}
                  />
                ),
              }}
              sx={{ ...fieldSx, width: 125, flexShrink: 0 }}
            >
              {dialCodes.map((entry) => (
                <Mui.MenuItem key={entry.code} value={entry.code}>
                  <DialCode entry={entry} />
                  <Mui.Box component="span" sx={{ ml: 1 }}>
                    {entry.country}
                  </Mui.Box>
                </Mui.MenuItem>
              ))}
            </Mui.TextField>
            <Mui.TextField
              fullWidth
              size="small"
              disabled={saving}
              type="tel"
              value={draft.phone}
              placeholder="Enter phone number"
              onChange={(event) =>
                update("phone", event.target.value.replace(/\D/g, ""))
              }
              error={Boolean(errors.phone)}
              inputProps={{
                "aria-label": "Phone",
                "aria-required": true,
                inputMode: "numeric",
              }}
              sx={fieldSx}
            />
          </Mui.Stack>
          {errors.phone && (
            <Mui.FormHelperText error>{errors.phone}</Mui.FormHelperText>
          )}
        </Mui.Box>
        <Mui.Box>
          <GlobalFieldLabel>
            Contact type
            <RequiredMark />
          </GlobalFieldLabel>
          <Mui.TextField
            select
            fullWidth
            size="small"
            disabled={saving}
            value={draft.contactType}
            onChange={(event) => update("contactType", event.target.value)}
            inputProps={{ "aria-label": "Contact type" }}
            sx={fieldSx}
          >
            {["Primary", "Secondary"].map((type) => (
              <Mui.MenuItem key={type} value={type}>
                {type}
              </Mui.MenuItem>
            ))}
          </Mui.TextField>
        </Mui.Box>
        <Mui.Box>
          <GlobalFieldLabel>WhatsApp</GlobalFieldLabel>
          <Mui.Stack direction="row" spacing={1}>
            <Mui.TextField
              select
              size="small"
              disabled={saving}
              value={draft.whatsappCode}
              onChange={(event) => update("whatsappCode", event.target.value)}
              inputProps={{ "aria-label": "WhatsApp country code" }}
              sx={{ ...fieldSx, width: 125, flexShrink: 0 }}
            >
              {dialCodes.map((entry) => (
                <Mui.MenuItem key={entry.code} value={entry.code}>
                  <DialCode entry={entry} />
                </Mui.MenuItem>
              ))}
            </Mui.TextField>
            <Mui.TextField
              fullWidth
              size="small"
              disabled={saving}
              type="tel"
              value={draft.whatsapp}
              onChange={(event) =>
                update("whatsapp", event.target.value.replace(/\D/g, ""))
              }
              error={Boolean(errors.whatsapp)}
              helperText={errors.whatsapp}
              inputProps={{ "aria-label": "WhatsApp", inputMode: "numeric" }}
              sx={fieldSx}
            />
          </Mui.Stack>
        </Mui.Box>
        <Mui.Box>
          <GlobalFieldLabel>
            Active
            <RequiredMark />
          </GlobalFieldLabel>
          <Mui.TextField
            select
            fullWidth
            size="small"
            disabled={saving}
            value={String(draft.active)}
            onChange={(event) =>
              update("active", event.target.value === "true")
            }
            inputProps={{ "aria-label": "Active" }}
            sx={fieldSx}
          >
            <Mui.MenuItem value="true">Active</Mui.MenuItem>
            <Mui.MenuItem value="false">Inactive</Mui.MenuItem>
          </Mui.TextField>
        </Mui.Box>
      </Mui.Stack>
    </GlobalSidePanel>
  );
}
